/**
 * CRM Dashboard — admin-only page showing all registered user profiles.
 * Accessible at /app/crm — redirects non-admins to home.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Users, Mail, Building2, Briefcase, TrendingUp,
  Download, Search, RefreshCw, Loader2, AlertTriangle,
  CheckCircle2, Calendar, Globe,
} from "lucide-react";
import { api, type CrmProfile } from "../api/client";

const ROLE_LABELS: Record<string, string> = {
  clinician: "Clinician / GP", researcher: "Researcher",
  developer: "Developer", student: "Student", other: "Other",
};
const ORG_LABELS: Record<string, string> = {
  nhs_trust: "NHS Trust", biotech: "Biotech / Pharma",
  university: "University", cro: "CRO", startup: "Startup", other: "Other",
};

function formatDate(ts: number): string {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function StatCard({ icon: Icon, label, value, color }: {
  icon: React.ElementType; label: string; value: string | number; color: string;
}) {
  return (
    <div className="ay-card">
      <div className={`w-10 h-10 rounded-xl bg-ay-surface border border-ay-border flex items-center justify-center mb-3`}>
        <Icon size={18} className={color} />
      </div>
      <div className="text-2xl font-black text-white">{value}</div>
      <div className="text-xs text-ay-muted mt-0.5">{label}</div>
    </div>
  );
}

export default function CrmDashboard() {
  const [profiles, setProfiles] = useState<CrmProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.crmListProfiles();
      setProfiles(res.profiles || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load profiles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = profiles.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      p.email?.toLowerCase().includes(q) ||
      p.first_name?.toLowerCase().includes(q) ||
      p.last_name?.toLowerCase().includes(q) ||
      p.org_name?.toLowerCase().includes(q);
    const matchRole = !roleFilter || p.role === roleFilter;
    return matchSearch && matchRole;
  });

  // Stats
  const total = profiles.length;
  const withOrg = profiles.filter((p) => p.org_name).length;
  const newsletter = profiles.filter((p) => p.newsletter).length;
  const roles = Object.entries(
    profiles.reduce((acc, p) => {
      acc[p.role || "other"] = (acc[p.role || "other"] || 0) + 1;
      return acc;
    }, {} as Record<string, number>)
  ).sort((a, b) => b[1] - a[1]);

  const exportCsv = () => {
    const headers = ["Email","First Name","Last Name","Role","Org","Org Type","Use Cases","Country","Newsletter","Signed Up"];
    const rows = profiles.map((p) => [
      p.email, p.first_name, p.last_name,
      ROLE_LABELS[p.role] || p.role,
      p.org_name, ORG_LABELS[p.org_type] || p.org_type,
      (p.use_cases || []).join("; "),
      p.country,
      p.newsletter ? "Yes" : "No",
      formatDate(p.created_at),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c || ""}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `alphayou-crm-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-white">
            <Users className="text-ay-cyan" /> CRM Dashboard
          </h1>
          <p className="text-sm text-ay-muted mt-1">
            Registered user profiles — admin only
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading}
            className="btn-ghost text-sm flex items-center gap-1.5">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button onClick={exportCsv} disabled={profiles.length === 0}
            className="btn-outline text-sm flex items-center gap-1.5">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users}     label="Total users"       value={total}       color="text-ay-cyan" />
        <StatCard icon={Building2} label="With organisation" value={withOrg}     color="text-violet-400" />
        <StatCard icon={Mail}      label="Newsletter opt-in" value={newsletter}  color="text-ay-emerald" />
        <StatCard icon={TrendingUp} label="Top role"
          value={roles[0] ? `${ROLE_LABELS[roles[0][0]] || roles[0][0]} (${roles[0][1]})` : "—"}
          color="text-ay-amber" />
      </div>

      {/* Role breakdown */}
      {roles.length > 0 && (
        <div className="ay-card">
          <h3 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
            <Briefcase size={15} className="text-ay-cyan" /> Role breakdown
          </h3>
          <div className="flex flex-wrap gap-2">
            {roles.map(([role, count]) => (
              <button key={role}
                onClick={() => setRoleFilter(roleFilter === role ? "" : role)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                  roleFilter === role
                    ? "border-ay-cyan bg-ay-cyan/10 text-ay-cyan"
                    : "border-ay-border text-ay-muted hover:border-ay-cyan/30"
                }`}
              >
                {ROLE_LABELS[role] || role} · {count}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ay-muted" />
        <input
          className="input pl-9"
          placeholder="Search by name, email, or organisation…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-xl p-3">
          <AlertTriangle size={15} /> {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16 text-ay-muted">
          <Loader2 size={24} className="animate-spin mr-2" /> Loading profiles…
        </div>
      )}

      {/* Empty */}
      {!loading && !error && filtered.length === 0 && (
        <div className="text-center py-16 text-ay-muted">
          <Users size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">{search || roleFilter ? "No profiles match your filter." : "No profiles yet."}</p>
        </div>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div className="ay-card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ay-border">
                  {["User", "Role", "Organisation", "Use Cases", "Country", "Newsletter", "Signed Up"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-ay-muted uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => (
                  <motion.tr key={p.user_sub}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className="border-b border-ay-border/50 hover:bg-white/3 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">
                        {p.first_name} {p.last_name}
                      </div>
                      <div className="text-xs text-ay-muted flex items-center gap-1 mt-0.5">
                        <Mail size={10} /> {p.email}
                      </div>
                      {p.phone && (
                        <div className="text-xs text-ay-muted mt-0.5">{p.phone}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="badge-cyan text-[10px]">
                        {ROLE_LABELS[p.role] || p.role || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white text-xs">{p.org_name || "—"}</div>
                      {p.org_type && (
                        <div className="text-ay-muted text-[10px] mt-0.5">
                          {ORG_LABELS[p.org_type] || p.org_type}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(p.use_cases || []).slice(0, 3).map((u) => (
                          <span key={u} className="text-[10px] bg-ay-surface border border-ay-border px-1.5 py-0.5 rounded text-ay-muted">
                            {u.replace(/_/g, " ")}
                          </span>
                        ))}
                        {(p.use_cases || []).length > 3 && (
                          <span className="text-[10px] text-ay-muted">+{p.use_cases.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs text-ay-muted">
                        <Globe size={11} /> {p.country || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {p.newsletter
                        ? <CheckCircle2 size={15} className="text-ay-emerald" />
                        : <span className="text-ay-muted text-xs">No</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs text-ay-muted">
                        <Calendar size={11} /> {formatDate(p.created_at)}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-ay-border text-xs text-ay-muted">
            Showing {filtered.length} of {total} profiles
          </div>
        </div>
      )}
    </div>
  );
}
